import { useState, useMemo, useEffect, useRef, type DragEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Loader2,
  ShieldCheck,
  X,
  Upload,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FolderTree,
  Library,
} from 'lucide-react';
import { toast } from 'sonner';
import { binder, documentTypes, siteUsers, docLibrary, studies, ApiError, type BinderDocumentRow } from '@/api/client';
import { humanRole } from '@/lib/humanise';
import { labelForRequiredField } from '@/lib/requiredFieldOptions';
import { SingleSelectSearch, type SingleSelectOption } from '@/components/ui/SingleSelectSearch';
import {
  parseRequiredFields,
  RequiredMetadataFields,
  EffectiveExpiryDateFields,
  TrainingRequiredToggle,
} from './TrainingUploadFields';
import { studyLabel } from '@/lib/studyLabel';

export interface NewDocumentSlotDialogProps {
  binderId: string;
  folderId: string;
  folderLabel: string;
  /** 'zone' | 'section' | 'artifact' | 'subartifact' — artifact means folderLabel IS the doc type */
  folderLevel?: string;
  open: boolean;
  onClose: () => void;
  onCreated: (doc: BinderDocumentRow) => void;
  /**
   * Does the caller hold binder.upload? Filing a document is its own grant,
   * split from binder.update on 2026-10-09 — a role may be allowed to open
   * this dialog and create the slot (binder.create) while not being allowed
   * to attach the file. Defaults true so existing callers are unaffected.
   */
  canUploadVersion?: boolean;
}

const ALLOWED_MIME = new Set(['application/pdf']);
const MAX_MB = 50;

function guessMime(_name: string): string {
  return 'application/pdf';
}

async function sha256OfFile(file: File): Promise<string | null> {
  try {
    const buf = await file.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return null;
  }
}

async function uploadWithProgress(
  url: string,
  file: File,
  mime: string,
  onProgress: (pct: number) => void,
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', mime);
    xhr.setRequestHeader('x-amz-server-side-encryption', 'AES256');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`S3 upload failed: ${xhr.status}`));
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(file);
  });
}

export function NewDocumentSlotDialog({
  binderId,
  folderId,
  folderLabel,
  folderLevel,
  canUploadVersion = true,
  open,
  onClose,
  onCreated,
}: NewDocumentSlotDialogProps) {
  const isArtifact = folderLevel === 'artifact';

  // ── Destination folder override (client feedback 2026-07-27 #3) ───────
  // The dialog used to file everything to the folder the user clicked
  // from — leading to accidental uploads into "ISF/eISF Index" when a
  // user landed there via the sidebar's default redirect. We now let the
  // user re-pick Zone / Section / Artifact before uploading.
  const [effectiveFolderId, setEffectiveFolderId] = useState(folderId);
  const [showChangeFolder, setShowChangeFolder] = useState(false);
  const [pickedZone, setPickedZone] = useState<string | null>(null);
  const [pickedSection, setPickedSection] = useState<string | null>(null);
  const [pickedArtifact, setPickedArtifact] = useState<string | null>(null);

  // Load the full folder tree so we can build the cascading pickers +
  // display the resolved path back to the user.
  const binderTreeQ = useQuery({
    queryKey: ['binder-tree-for-picker', binderId],
    queryFn: () => binder.get(binderId),
    staleTime: 5 * 60_000,
    enabled: open,
  });
  const allFolders = binderTreeQ.data?.folders ?? [];

  // Bug found live (UAT: "EISF Upload Document is showing Study field and
  // required. When the binder is specific to the study, the study should
  // be linked by default and not allowing users to fill in."). The binder
  // is already permanently scoped to one study — resolve its label here so
  // the catalog's generic "study" required field can be shown read-only
  // instead of asking the uploader to retype it.
  const studiesQ = useQuery({
    queryKey: ['studies'],
    queryFn: () => studies.list(),
    enabled: open,
    staleTime: 60_000,
  });
  const boundStudyId = binderTreeQ.data?.binder.study_id;
  const boundStudy = studiesQ.data?.rows.find((s) => s.study_id === boundStudyId);
  const studyDisplay = boundStudy ? studyLabel(boundStudy) : undefined;

  // Seed the cascade with the current effective folder's zone/section
  // whenever the dialog opens (or its target folder changes).
  useEffect(() => {
    const cur = allFolders.find((f) => f.folder_id === effectiveFolderId);
    if (cur) {
      setPickedZone(cur.zone_name);
      setPickedSection(cur.section_name);
      setPickedArtifact(cur.artifact_type);
    }
  }, [effectiveFolderId, allFolders]);

  const zoneOptions = useMemo(
    () => Array.from(new Set(allFolders.map((f) => f.zone_name).filter(Boolean))).sort(),
    [allFolders],
  );
  const sectionOptions = useMemo(
    () =>
      Array.from(
        new Set(
          allFolders
            .filter((f) => f.zone_name === pickedZone)
            .map((f) => f.section_name)
            .filter(Boolean),
        ),
      ).sort(),
    [allFolders, pickedZone],
  );
  const artifactOptions = useMemo(
    () =>
      Array.from(
        new Set(
          allFolders
            .filter((f) => f.zone_name === pickedZone && f.section_name === pickedSection)
            .map((f) => f.artifact_type)
            .filter(Boolean),
        ),
      ).sort(),
    [allFolders, pickedZone, pickedSection],
  );

  function applyFolderChange() {
    const target = allFolders.find(
      (f) =>
        f.zone_name === pickedZone &&
        f.section_name === pickedSection &&
        f.artifact_type === pickedArtifact,
    );
    if (target) {
      setEffectiveFolderId(target.folder_id);
      setShowChangeFolder(false);
    }
  }

  const currentFolder = allFolders.find((f) => f.folder_id === effectiveFolderId);
  const currentPath = currentFolder
    ? [currentFolder.zone_name, currentFolder.section_name, currentFolder.artifact_type]
        .filter(Boolean)
        .join(' → ')
    : folderLabel;
  const isOverridden = effectiveFolderId !== folderId;

  // ── State ────────────────────────────────────────────────────────────
  const [documentType, setDocumentType] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [phiPresent, setPhiPresent] = useState(false);
  const [assigneeUserId, setAssigneeUserId] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  // Supersession fields — required by the BE when uploading over a slot
  // whose current version is `current`. FR-DOC-08.
  const [changeReason, setChangeReason] = useState<
    'replace' | 'amendment' | 'correction' | 'other' | ''
  >('');
  const [changeReasonDetails, setChangeReasonDetails] = useState('');
  // Training module Phase 2 — per-upload override of the doc type's catalog
  // training defaults. Phase 3 — required-field capture + effective/expiry
  // dates, sourced from the doc type's catalog config. Reset after each
  // successful upload / dialog reopen.
  const [trainingRequired, setTrainingRequired] = useState(false);
  const [trainingRoles, setTrainingRoles] = useState<string[]>([]);
  const [metadataFields, setMetadataFields] = useState<Record<string, string>>({});
  const [effectiveAt, setEffectiveAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Document Library "pick from library" integration — an alternative to
  // uploading a fresh file. Shares every metadata field above; only the
  // file-transport step differs (no bytes leave the browser, the backend
  // copies the object server-side).
  const [pickingFromLibrary, setPickingFromLibrary] = useState(false);
  const [libraryFileId, setLibraryFileId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  // Once a slot row exists the folder list must know about it, so that a
  // retry after a failed presign/upload/finalise reuses the slot instead of
  // hitting the BE's duplicate guard (409) on a second createSlot.
  const noteSlotCreated = () =>
    queryClient.invalidateQueries({ queryKey: ['binder-folder-documents', binderId, effectiveFolderId] });

  // The dialog is a single reused instance across every folder/document type
  // (no `key` prop forces a remount) — without resetting on both transitions,
  // stale values (change reason, title, etc. from whatever slot was open
  // before) leak into the next open, and uploadMut's `isSuccess` from a
  // previous upload shows "Uploaded!" immediately even though nothing was
  // uploaded here yet.
  useEffect(() => {
    setDocumentType(open && isArtifact ? folderLabel : null);
    setArtifactAutoResolved(isArtifact);
    setTitle('');
    setPhiPresent(false);
    setAssigneeUserId(null);
    setAdvancedOpen(false);
    setDragOver(false);
    setUploadProgress(null);
    setChangeReason('');
    setChangeReasonDetails('');
    setTrainingRequired(false);
    setTrainingRoles([]);
    setMetadataFields({});
    setEffectiveAt('');
    setExpiresAt('');
    // Reset the folder-override state when the dialog reopens for a new
    // slot — otherwise a stale override from a previous open leaks into
    // the next click.
    setEffectiveFolderId(folderId);
    setShowChangeFolder(false);
    setPickingFromLibrary(false);
    setLibraryFileId(null);
    uploadMut.reset();
    fileFromLibraryMut.reset();
  }, [open, folderId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Folder-scoped doc types (non-artifact folders only) ─────────────
  // Artifact folders use folderLabel directly — no catalog lookup needed.
  const folderTypesQ = useQuery({
    queryKey: ['binder-folder-document-types', binderId, effectiveFolderId],
    queryFn: () => binder.listFolderDocumentTypes(binderId, effectiveFolderId),
    staleTime: 5 * 60_000,
    enabled: open && !isArtifact,
  });

  const folderTypes = folderTypesQ.data?.document_types ?? [];

  // For artifact folders: the folder display_name IS the document type.
  // Auto-set it immediately — no picker, no catalog lookup needed.
  const [artifactAutoResolved, setArtifactAutoResolved] = useState(isArtifact);
  useEffect(() => {
    if (isArtifact) {
      setDocumentType(folderLabel);
      setArtifactAutoResolved(true);
    }
  }, [isArtifact, folderLabel]);

  // "Change destination folder…" re-targets the upload, so the document
  // type — and with it the catalog's required fields and approver — must
  // follow the chosen folder, not the one the dialog was opened from.
  // Otherwise an Insurance upload filed from the ISF Index folder would be
  // typed (and validated) as an ISF/eISF Index.
  useEffect(() => {
    if (!isOverridden) return;
    if (!currentFolder) return;
    const overriddenIsArtifact =
      currentFolder.folder_level === 'artifact' || currentFolder.folder_level === 'subartifact';
    setDocumentType(
      overriddenIsArtifact ? currentFolder.display_name || currentFolder.artifact_type || null : null,
    );
    setArtifactAutoResolved(overriddenIsArtifact);
    setMetadataFields({});
  }, [isOverridden, currentFolder?.folder_id]); // eslint-disable-line react-hooks/exhaustive-deps


  // ── Site users (for assignee) ────────────────────────────────────────
  const siteUsersQ = useQuery({
    queryKey: ['site-users', 'me'],
    queryFn: siteUsers.listMine,
    staleTime: 60_000,
    enabled: open && advancedOpen,
  });
  const assigneeOptions = useMemo<SingleSelectOption[]>(
    () =>
      (siteUsersQ.data?.users ?? []).map((u) => ({
        value: u.user_id,
        label: u.full_name,
        sublabel: u.email + (u.status !== 'active' ? ` (${u.status})` : ''),
      })),
    [siteUsersQ.data],
  );

  // ── Approver lookup ─────────────────────────────────────────────────
  // Resolve the doc-type CODE (URL-safe kebab-case slug) from the NAME.
  // Passing the raw name breaks the endpoint for any type whose name
  // contains `/` (e.g. "ISF/eISF Index") because HttpApi doesn't route
  // %2F in path params. Consult the folder-scoped list first (populated
  // for non-artifact folders); fall through to the full catalog for
  // artifact folders where folderTypes is disabled.
  const allDocTypesQ = useQuery({
    queryKey: ['document-types', 'list'],
    queryFn: () => documentTypes.list(),
    staleTime: 10 * 60_000,
    enabled: open,
  });

  /**
   * Feedback (2026-09-29, Binder catalog item 5) — "auto default routing
   * leaves no upload option".
   *
   * On an artifact folder the dialog forces the document type to the
   * FOLDER'S OWN NAME and hides the picker entirely. On a sponsor's custom
   * folder that means a protocol uploaded into "Sponsor Study Plan" gets
   * typed "Sponsor Study Plan" — off-catalog, so no lifecycle, no required
   * fields, no approver, and no way for the user to say otherwise.
   *
   * When the folder carries a mapping (CR-2026-09-29), that mapping names
   * the real catalog types this folder is meant to hold. Offer those and
   * let the user choose, instead of deciding for them.
   */
  const mappedDocTypes = useMemo(() => {
    const raw = currentFolder?.mapped_doc_type_codes;
    if (!raw) return [];
    let codes: string[] = [];
    try {
      const parsed: unknown = JSON.parse(raw);
      codes = Array.isArray(parsed) ? parsed.filter((c): c is string => typeof c === 'string') : [];
    } catch {
      return [];
    }
    if (codes.length === 0) return [];
    return (allDocTypesQ.data?.document_types ?? []).filter((t) =>
      codes.includes(t.document_type_code),
    );
  }, [currentFolder?.mapped_doc_type_codes, allDocTypesQ.data]);

  // Once the mapped types are known, hand control back to the user. Guarded
  // on the current value so a deliberate pick is never overwritten by a
  // late-arriving catalog fetch.
  useEffect(() => {
    if (!open || mappedDocTypes.length === 0) return;
    setArtifactAutoResolved(false);
    const first = mappedDocTypes[0];
    if (!first) return;
    setDocumentType((cur) => (cur === null || cur === folderLabel ? first.document_type_name : cur));
  }, [open, mappedDocTypes, folderLabel]);

  const typeOptions = useMemo<SingleSelectOption[]>(() => {
    const shorten = (d: string | null | undefined) =>
      d && d.length > 120 ? d.slice(0, 117) + '…' : d || undefined;
    // A mapped artifact folder offers its mapped catalog types, plus the
    // folder's own name so an off-catalog document can still be filed here.
    if (mappedDocTypes.length > 0) {
      return [
        ...mappedDocTypes.map((t) => ({
          value: t.document_type_name,
          label: t.document_type_name,
          sublabel: shorten(t.definition),
        })),
        {
          value: folderLabel,
          label: folderLabel,
          sublabel: 'This folder’s own name — use for anything outside the catalog',
        },
      ];
    }
    return folderTypes.map((t) => ({
      value: t.document_type_name,
      label: t.document_type_name,
      sublabel: shorten(t.definition),
    }));
  }, [folderTypes, mappedDocTypes, folderLabel]);

  const documentTypeCode = useMemo(() => {
    if (!documentType) return null;
    const fromFolder = folderTypes.find(
      (t) => t.document_type_name === documentType,
    )?.document_type_code;
    if (fromFolder) return fromFolder;
    return (
      (allDocTypesQ.data?.document_types ?? []).find((t) => t.document_type_name === documentType)
        ?.document_type_code ?? null
    );
  }, [documentType, folderTypes, allDocTypesQ.data]);

  const approversQ = useQuery({
    queryKey: ['document-type-approvers', documentTypeCode],
    queryFn: () => documentTypes.getApprovers(documentTypeCode!),
    enabled: !!documentTypeCode && open,
    staleTime: 5 * 60_000,
    retry: false,
  });

  // ── Existing slots in this folder ────────────────────────────────────
  // Detect empty draft slots (status='draft', current_version_id=null)
  // for the same doc-type — reuse them instead of hitting the BE's
  // duplicate guard with a 409. Slot creation is intentionally strict
  // (one non-terminal slot per (folder, doc-type)); we surface the
  // existing slot so the user can drop their file into it directly.
  const folderDocsQ = useQuery({
    queryKey: ['binder-folder-documents', binderId, effectiveFolderId],
    queryFn: () => binder.listFolderDocuments(binderId, effectiveFolderId),
    enabled: open,
    staleTime: 30_000,
  });

  // Any existing slot for the same doc-type in this folder (any status).
  // A new upload on top of it should append a new version — the BE's
  // presignVersionUpload auto-increments version_label to v{N+1}.0.
  const existingSlot = useMemo(() => {
    if (!documentType) return null;
    return folderDocsQ.data?.documents.find((d) => d.document_type_name === documentType) ?? null;
  }, [folderDocsQ.data, documentType]);

  // Terminal states that must NOT accept a new version — the caller has to
  // explicitly re-open the doc first (from the version detail actions).
  // Locked and superseded slots are allowed to accept a new version upload;
  // only in_approval (and archived) still block until the doc is re-opened.
  const BLOCKED_STATES = new Set(['in_approval', 'archived']);
  const existingSlotBlocks = existingSlot && BLOCKED_STATES.has(existingSlot.status);

  // Document-TYPE catalog lock (Admin → eISF Catalog:
  // create_new_draft_versions_bool = false). Mirrors the backend's own
  // gate in presignVersionUpload.ts — checked here too so the dialog can
  // disable the upload control up front with a clear reason instead of
  // letting the user pick a file and hit a raw rejection after the fact.
  const matchedDocType = documentType
    ? (allDocTypesQ.data?.document_types ?? []).find((t) => t.document_type_name === documentType)
    : undefined;
  const docTypeLocked = matchedDocType ? !matchedDocType.create_new_draft_versions_bool : false;
  // Training module Phase 3 — required-field keys declared on the catalog
  // row for this document type. Empty for types with no configured fields.
  const requiredFieldKeys = useMemo(
    () => parseRequiredFields(matchedDocType?.required_fields_json),
    [matchedDocType],
  );
  // The "study" required field is bound silently from the binder's own
  // study — never shown to the uploader, never able to block the upload
  // on a slow studies.list() fetch (the server stamps it again from the
  // binder at finalise time regardless of what the client sends).
  const visibleRequiredFieldKeys = useMemo(
    () => requiredFieldKeys.filter((k) => k !== 'study'),
    [requiredFieldKeys],
  );
  useEffect(() => {
    if (requiredFieldKeys.includes('study') && studyDisplay) {
      setMetadataFields((prev) => (prev.study === studyDisplay ? prev : { ...prev, study: studyDisplay }));
    }
  }, [requiredFieldKeys, studyDisplay]);
  const existingSlotVersionsQ = useQuery({
    queryKey: ['binder-versions', binderId, existingSlot?.document_id],
    queryFn: () => binder.listVersions(binderId, existingSlot!.document_id),
    enabled: open && docTypeLocked && !!existingSlot,
    staleTime: 30_000,
  });
  const CATALOG_LOCKED_STATUSES = new Set(['in_approval', 'current', 'superseded', 'archived']);
  const catalogLocked =
    docTypeLocked &&
    !!existingSlot &&
    (existingSlotVersionsQ.data?.versions ?? []).some((v) => CATALOG_LOCKED_STATUSES.has(v.status));

  // ── Upload mutation (create slot + presign + upload + finalise) ─────
  const uploadMut = useMutation({
    mutationFn: async (file: File) => {
      const docTypeName = documentType;
      if (!docTypeName) throw new Error('No document type selected');
      const mime = file.type || guessMime(file.name);

      if (existingSlotBlocks) {
        // in_approval / archived slots refuse a new version — the doc has
        // to be re-opened first.
        throw new Error(
          `Slot ${existingSlot.reference_number ?? ''} is ${existingSlot.status.replace(
            '_',
            ' ',
          )} — can't append a version. Re-open the doc from its version detail before uploading.`,
        );
      }

      if (catalogLocked) {
        throw new Error(
          `"${docTypeName}" is a locked document type — it cannot be replaced with a new version upload. ` +
            'Contact your site admin if a replacement is required.',
        );
      }

      // Reuse the existing slot when one is present (draft / preview /
      // current). Skips createSlot (which would 409 on the BE's duplicate
      // guard) and heads straight into presign + upload + finalise against
      // the existing document_id. The BE mints v{N+1}.0 automatically.
      let docId: string;
      let slotForCallback: BinderDocumentRow;
      if (existingSlot) {
        docId = existingSlot.document_id;
        slotForCallback = existingSlot;
      } else {
        const slotRes = await binder.createSlot(binderId, effectiveFolderId, {
          document_type_name: docTypeName,
          ...(title.trim() ? { title: title.trim() } : {}),
          phi_present: phiPresent,
          ...(assigneeUserId ? { assignee_user_id: assigneeUserId } : {}),
        });
        docId = slotRes.document.document_id;
        slotForCallback = slotRes.document;
        void noteSlotCreated();
      }

      const presigned = await binder.presignVersion(binderId, docId, {
        file_name: file.name,
        mime_type: mime,
      });

      const clientSha256 = await sha256OfFile(file);
      await uploadWithProgress(presigned.upload_url, file, mime, setUploadProgress);

      await binder.finaliseVersion(binderId, docId, {
        version_id: presigned.version_id,
        version_label: presigned.version_label,
        s3_bucket: presigned.s3_bucket,
        s3_key: presigned.s3_key,
        file_name: file.name,
        file_size: file.size,
        mime_type: mime,
        ...(clientSha256 ? { client_sha256: clientSha256 } : {}),
        // Only forward supersession fields when we know the upload will
        // supersede a `current` version. BE ignores otherwise.
        ...(existingSlot?.status === 'current' && changeReason
          ? {
              change_reason: changeReason,
              ...(changeReasonDetails.trim()
                ? { change_reason_details: changeReasonDetails.trim() }
                : {}),
            }
          : {}),
        // Training module Phase 2/3 — per-upload override + required
        // metadata. Omit training fields when untouched so the catalog
        // default applies unchanged.
        ...(trainingRequired ? { training_required: true } : {}),
        ...(trainingRequired && trainingRoles.length > 0
          ? { training_required_roles: trainingRoles }
          : {}),
        ...(effectiveAt ? { effective_at: new Date(effectiveAt).toISOString() } : {}),
        ...(expiresAt ? { version_expires_at: new Date(expiresAt).toISOString() } : {}),
        ...(requiredFieldKeys.length > 0 ? { metadata_fields: metadataFields } : {}),
      });

      return slotForCallback;
    },
    onSuccess: (doc) => {
      toast.success(
        existingSlot
          ? `Uploaded as new version on ${existingSlot.reference_number ?? 'existing slot'}`
          : 'Uploaded successfully',
      );
      onCreated(doc);
      onClose();
    },
    onError: (err) => {
      setUploadProgress(null);
      if (err instanceof ApiError && err.status === 403) {
        toast.error("You don't have permission to upload documents in this binder");
      } else {
        toast.error(err instanceof Error ? err.message : 'Upload failed');
      }
    },
  });

  // ── Document Library "pick from library" integration ────────────────
  // Populated only while the picker is open — no reason to fetch the
  // site-wide file/folder lists on every dialog open.
  const libraryFilesQ = useQuery({
    queryKey: ['doc-library-files', 'all'],
    queryFn: () => docLibrary.listFiles(),
    staleTime: 30_000,
    enabled: open && pickingFromLibrary,
  });
  const libraryFoldersQ = useQuery({
    queryKey: ['doc-library-folders'],
    queryFn: () => docLibrary.listFolders(),
    staleTime: 60_000,
    enabled: open && pickingFromLibrary,
  });
  const libraryFolderNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of libraryFoldersQ.data?.folders ?? []) map.set(f.folder_id, f.name);
    return map;
  }, [libraryFoldersQ.data]);
  const libraryFileOptions = useMemo<SingleSelectOption[]>(
    () =>
      (libraryFilesQ.data?.files ?? [])
        .filter((f) => f.mime_type === 'application/pdf')
        .map((f) => ({
          value: f.file_id,
          label: f.file_name,
          sublabel: f.folder_id ? (libraryFolderNameById.get(f.folder_id) ?? 'Unknown folder') : 'Home',
        })),
    [libraryFilesQ.data, libraryFolderNameById],
  );

  const fileFromLibraryMut = useMutation({
    mutationFn: async () => {
      const docTypeName = documentType;
      if (!docTypeName) throw new Error('No document type selected');
      if (!libraryFileId) throw new Error('No library file selected');

      if (existingSlotBlocks) {
        throw new Error(
          `Slot ${existingSlot.reference_number ?? ''} is ${existingSlot.status.replace(
            '_',
            ' ',
          )} — can't append a version. Re-open the doc from its version detail before uploading.`,
        );
      }

      if (catalogLocked) {
        throw new Error(
          `"${docTypeName}" is a locked document type — it cannot be replaced with a new version upload. ` +
            'Contact your site admin if a replacement is required.',
        );
      }

      let docId: string;
      let slotForCallback: BinderDocumentRow;
      if (existingSlot) {
        docId = existingSlot.document_id;
        slotForCallback = existingSlot;
      } else {
        const slotRes = await binder.createSlot(binderId, effectiveFolderId, {
          document_type_name: docTypeName,
          ...(title.trim() ? { title: title.trim() } : {}),
          phi_present: phiPresent,
          ...(assigneeUserId ? { assignee_user_id: assigneeUserId } : {}),
        });
        docId = slotRes.document.document_id;
        slotForCallback = slotRes.document;
        void noteSlotCreated();
      }

      await binder.finaliseVersionFromLibrary(binderId, docId, {
        library_file_id: libraryFileId,
        ...(existingSlot?.status === 'current' && changeReason
          ? {
              change_reason: changeReason,
              ...(changeReasonDetails.trim()
                ? { change_reason_details: changeReasonDetails.trim() }
                : {}),
            }
          : {}),
        ...(trainingRequired ? { training_required: true } : {}),
        ...(trainingRequired && trainingRoles.length > 0
          ? { training_required_roles: trainingRoles }
          : {}),
        ...(effectiveAt ? { effective_at: new Date(effectiveAt).toISOString() } : {}),
        ...(expiresAt ? { version_expires_at: new Date(expiresAt).toISOString() } : {}),
        ...(requiredFieldKeys.length > 0 ? { metadata_fields: metadataFields } : {}),
      });

      return slotForCallback;
    },
    onSuccess: (doc) => {
      toast.success(
        existingSlot
          ? `Filed from library as new version on ${existingSlot.reference_number ?? 'existing slot'}`
          : 'Filed from library successfully',
      );
      onCreated(doc);
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 403) {
        toast.error("You don't have permission to upload documents in this binder");
      } else {
        toast.error(err instanceof Error ? err.message : 'Failed to file from library');
      }
    },
  });

  function handleFile(file: File) {
    if (!ALLOWED_MIME.has(file.type || guessMime(file.name))) {
      toast.error('Only PDF files are accepted.');
      return;
    }
    if (file.size === 0) {
      toast.error('Cannot upload an empty file.');
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File must be under ${MAX_MB} MB.`);
      return;
    }
    const missingFields = requiredFieldKeys.filter((k) => k !== 'study' && !metadataFields[k]?.trim());
    if (missingFields.length > 0) {
      toast.error(
        `Fill in the required field(s) before uploading: ${missingFields.map(labelForRequiredField).join(', ')}`,
      );
      return;
    }
    uploadMut.mutate(file);
  }

  function handleFileFromLibrary() {
    if (!libraryFileId) {
      toast.error('Pick a file from the library first.');
      return;
    }
    const missingFields = requiredFieldKeys.filter((k) => k !== 'study' && !metadataFields[k]?.trim());
    if (missingFields.length > 0) {
      toast.error(
        `Fill in the required field(s) before filing: ${missingFields.map(labelForRequiredField).join(', ')}`,
      );
      return;
    }
    fileFromLibraryMut.mutate();
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  if (!open) return null;

  const isUploading =
    uploadMut.isPending ||
    fileFromLibraryMut.isPending ||
    (uploadProgress !== null && uploadProgress < 100);
  // Supersession over a Current slot needs a change reason (BE also
  // enforces — this gate just keeps the click from failing the whole
  // upload dance halfway through).
  const needsChangeReason = existingSlot?.status === 'current' && !changeReason;
  // Block upload until the existing-slot check has actually loaded — dropping
  // a file in that window meant existingSlot was still null (query hadn't
  // resolved yet), so the code took the "create new slot" path and hit the
  // BE's duplicate-slot guard (409) instead of appending a version to the
  // slot that was there all along.
  const canUpload =
    canUploadVersion &&
    !!documentType &&
    !isUploading &&
    !needsChangeReason &&
    !folderDocsQ.isLoading &&
    !catalogLocked &&
    !(docTypeLocked && existingSlot && existingSlotVersionsQ.isLoading);

  // Hide the picker when: still loading, OR artifact folder resolved to a single type.
  // Show the picker when: non-artifact folder, OR artifact folder name didn't match catalog.
  // A mapped folder always shows the picker — that IS the "upload option"
  // the auto-routing used to take away (Feedback, Binder catalog item 5).
  const showTypeLoader = folderTypesQ.isLoading && !documentType;
  const showTypePicker =
    mappedDocTypes.length > 0 ||
    (!folderTypesQ.isLoading && !(isArtifact && artifactAutoResolved));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center !m-0 bg-black/60 backdrop-blur-sm p-4 !m-0"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isUploading) onClose();
      }}
    >
      <div className="card w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-ink-primary">Upload document</h2>
            <p className="mt-0.5 text-xs text-ink-muted">
              Filing to:{' '}
              <span
                className={`font-medium ${isOverridden ? 'text-accent-700' : 'text-ink-primary'}`}
              >
                {currentPath}
              </span>
              {isOverridden && (
                <span className="ml-1 rounded bg-accent-100 px-1 py-0.5 text-[9px] font-semibold text-accent-700">
                  changed
                </span>
              )}
            </p>
            <button
              type="button"
              onClick={() => setShowChangeFolder((v) => !v)}
              disabled={isUploading}
              className="mt-1 inline-flex items-center gap-0.5 text-[11px] text-accent-700 hover:underline disabled:opacity-40"
            >
              <FolderTree className="h-3 w-3" />
              {showChangeFolder ? 'Hide folder picker' : 'Change destination folder…'}
            </button>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isUploading}
            className="text-ink-muted hover:text-ink-primary disabled:opacity-40 shrink-0"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Folder-change picker — client feedback #3, always available so
            users can re-file the upload if the auto-selected folder is
            wrong (e.g. accidentally landed on ISF/eISF Index). */}
        {showChangeFolder && (
          <div className="rounded border border-accent-200 bg-accent-50/40 p-3 space-y-2">
            <div className="text-[11px] font-medium text-ink-primary">
              Pick where this document should be filed
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <label className="block text-[11px]">
                <span className="font-medium text-ink-secondary">Zone</span>
                <select
                  className="input mt-0.5 text-xs"
                  value={pickedZone ?? ''}
                  onChange={(e) => {
                    setPickedZone(e.target.value || null);
                    setPickedSection(null);
                    setPickedArtifact(null);
                  }}
                >
                  <option value="">— pick a zone —</option>
                  {zoneOptions.map((z) => (
                    <option key={z} value={z}>
                      {z}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-[11px]">
                <span className="font-medium text-ink-secondary">Section</span>
                <select
                  className="input mt-0.5 text-xs"
                  disabled={!pickedZone}
                  value={pickedSection ?? ''}
                  onChange={(e) => {
                    setPickedSection(e.target.value || null);
                    setPickedArtifact(null);
                  }}
                >
                  <option value="">— pick a section —</option>
                  {sectionOptions.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-[11px]">
                <span className="font-medium text-ink-secondary">Artifact type</span>
                <select
                  className="input mt-0.5 text-xs"
                  disabled={!pickedSection}
                  value={pickedArtifact ?? ''}
                  onChange={(e) => setPickedArtifact(e.target.value || null)}
                >
                  <option value="">— pick an artifact —</option>
                  {artifactOptions.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowChangeFolder(false)}
                className="rounded border border-surface-border px-2 py-1 text-[11px] font-medium hover:bg-surface-muted/50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyFolderChange}
                disabled={!pickedZone || !pickedSection || !pickedArtifact}
                className="rounded bg-accent-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-accent-700 disabled:opacity-50"
              >
                Use this folder
              </button>
            </div>
          </div>
        )}

        {/* Document type picker — only for non-artifact (section/zone) folders */}
        {showTypeLoader && (
          <div className="flex items-center gap-2 text-xs text-ink-muted">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading document types…
          </div>
        )}
        {showTypePicker && (
          <div>
            <label className="label text-xs" htmlFor="new-doc-type">
              Document type *
            </label>
            <SingleSelectSearch
              id="new-doc-type"
              options={typeOptions}
              value={documentType}
              onChange={setDocumentType}
              placeholder="Search document types…"
              disabled={isUploading}
            />
          </div>
        )}

        {/* Supersession — required change reason when uploading over a
            current published slot (client spec FR-DOC-08). */}
        {existingSlot?.status === 'current' && (
          <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 space-y-2">
            <div className="text-xs font-medium text-amber-900">
              This upload will supersede the current version
            </div>
            <p className="text-[11px] text-amber-800">
              {existingSlot.reference_number ?? 'The current version'} is Published. A change reason
              is required. Prior version stays accessible as Superseded in the version history.
            </p>
            <div>
              <label className="label text-[11px]" htmlFor="new-doc-change-reason">
                Change reason *
              </label>
              <select
                id="new-doc-change-reason"
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value as typeof changeReason)}
                className="input text-xs"
                disabled={isUploading}
              >
                <option value="">Select a reason…</option>
                <option value="replace">Replace (content unchanged, file replacement)</option>
                <option value="amendment">Amendment (protocol / substantive change)</option>
                <option value="correction">Correction (typo / clerical fix)</option>
                <option value="other">Other (specify below)</option>
              </select>
            </div>
            <div>
              <label className="label text-[11px]" htmlFor="new-doc-change-details">
                Details (optional)
              </label>
              <textarea
                id="new-doc-change-details"
                value={changeReasonDetails}
                onChange={(e) => setChangeReasonDetails(e.target.value)}
                rows={2}
                className="input text-xs resize-none"
                placeholder="Short description of what changed"
                disabled={isUploading}
              />
            </div>
          </div>
        )}

        {/* Approver info */}
        {documentType && (
          <div className="rounded border border-surface-border bg-surface-muted/30 px-3 py-2">
            <div className="flex items-center gap-2 text-xs font-medium text-ink-secondary">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-700" />
              Approver
            </div>
            {approversQ.isLoading ? (
              <div className="mt-1 flex items-center gap-1 text-[11px] text-ink-muted">
                <Loader2 className="h-3 w-3 animate-spin" /> Looking up…
              </div>
            ) : (approversQ.data?.approvers ?? []).length === 0 ? (
              <p className="mt-1 text-[11px] italic text-ink-muted">
                No approver configured — this document will not require approval.
              </p>
            ) : (
              <p className="mt-1 text-[11px] text-ink-primary">
                {(approversQ.data?.approvers ?? [])
                  .map((a: { role: string }) => humanRole(a.role))
                  .join(approversQ.data?.approval_mode === 'sequential' ? ' → ' : ', ')}
              </p>
            )}
          </div>
        )}

        {/* Training module Phase 2/3 — required metadata, effective/expiry
            dates, and per-upload training override. Only meaningful once a
            document type is selected. */}
        {documentType && (
          <>
            {visibleRequiredFieldKeys.length > 0 && (
              <RequiredMetadataFields
                fieldKeys={visibleRequiredFieldKeys}
                values={metadataFields}
                onChange={(key, value) =>
                  setMetadataFields((prev) => ({ ...prev, [key]: value }))
                }
                studyId={boundStudyId}
              />
            )}
            <EffectiveExpiryDateFields
              effectiveAt={effectiveAt}
              onEffectiveAtChange={setEffectiveAt}
              expiresAt={expiresAt}
              onExpiresAtChange={setExpiresAt}
            />
            <TrainingRequiredToggle
              checked={trainingRequired}
              onCheckedChange={setTrainingRequired}
              roles={trainingRoles}
              onRolesChange={setTrainingRoles}
            />
          </>
        )}

        {/* Source toggle — upload a fresh file vs. pick an existing one from
            the site-wide Document Library (client feedback 2026-08-04) so
            the same file doesn't have to be re-uploaded into every study's
            binder. */}
        <div className="flex gap-1 rounded-lg border border-surface-border bg-surface-muted/30 p-0.5 text-xs">
          <button
            type="button"
            onClick={() => setPickingFromLibrary(false)}
            disabled={isUploading}
            className={`flex-1 rounded-md px-2 py-1.5 font-medium transition-colors ${
              !pickingFromLibrary
                ? 'bg-white text-ink-primary shadow-sm'
                : 'text-ink-muted hover:text-ink-primary'
            }`}
          >
            <Upload className="mr-1 inline h-3.5 w-3.5" />
            Upload new file
          </button>
          <button
            type="button"
            onClick={() => setPickingFromLibrary(true)}
            disabled={isUploading}
            className={`flex-1 rounded-md px-2 py-1.5 font-medium transition-colors ${
              pickingFromLibrary
                ? 'bg-white text-ink-primary shadow-sm'
                : 'text-ink-muted hover:text-ink-primary'
            }`}
          >
            <Library className="mr-1 inline h-3.5 w-3.5" />
            Pick from Document Library
          </button>
        </div>

        {pickingFromLibrary ? (
          <div className="space-y-2">
            <div>
              <label className="label text-xs" htmlFor="new-doc-library-file">
                Library file *
              </label>
              <SingleSelectSearch
                id="new-doc-library-file"
                options={libraryFileOptions}
                value={libraryFileId}
                onChange={setLibraryFileId}
                placeholder={
                  libraryFilesQ.isLoading ? 'Loading library…' : 'Search library PDFs…'
                }
                disabled={isUploading || libraryFilesQ.isLoading}
              />
              {!libraryFilesQ.isLoading && libraryFileOptions.length === 0 && (
                <p className="mt-1 text-[11px] italic text-ink-muted">
                  No PDF files in the Document Library yet.
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={handleFileFromLibrary}
              disabled={!canUpload || !libraryFileId}
              className="w-full rounded-lg bg-accent-600 px-3 py-2 text-sm font-medium text-white hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {fileFromLibraryMut.isPending ? (
                <span className="inline-flex items-center justify-center gap-1.5">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Filing…
                </span>
              ) : fileFromLibraryMut.isSuccess ? (
                <span className="inline-flex items-center justify-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Filed!
                </span>
              ) : (
                'File this document'
              )}
            </button>
            <p className="text-center text-[11px] text-ink-muted">
              {catalogLocked
                ? `"${documentType}" is a locked document type — no new versions accepted. Contact your site admin if a replacement is required.`
                : !documentType
                  ? 'Select a document type above first'
                  : 'The file is copied server-side — nothing is re-uploaded.'}
            </p>
          </div>
        ) : (
          <div
            className={`relative flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-10 text-center transition-colors ${
              dragOver
                ? 'border-accent-500 bg-accent-50'
                : canUpload
                  ? 'cursor-pointer border-surface-border hover:border-accent-400 hover:bg-surface-muted/40'
                  : 'cursor-not-allowed border-surface-border opacity-50'
            }`}
            onClick={() => canUpload && fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              if (canUpload) setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              if (canUpload) handleDrop(e);
              else e.preventDefault();
            }}
          >
            {isUploading ? (
              <>
                <Loader2 className="h-8 w-8 animate-spin text-accent-500" />
                <p className="text-sm font-medium text-ink-primary">
                  Uploading…{uploadProgress !== null ? ` ${uploadProgress}%` : ''}
                </p>
                {uploadProgress !== null && (
                  <div className="h-1.5 w-48 overflow-hidden rounded-full bg-surface-border">
                    <div
                      className="h-full rounded-full bg-accent-500 transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                )}
              </>
            ) : uploadMut.isSuccess ? (
              <>
                <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                <p className="text-sm font-medium text-emerald-700">Uploaded!</p>
              </>
            ) : (
              <>
                {folderDocsQ.isLoading ||
                (docTypeLocked && existingSlot && existingSlotVersionsQ.isLoading) ? (
                  <Loader2 className="h-8 w-8 animate-spin text-ink-muted" />
                ) : (
                  <Upload className="h-8 w-8 text-ink-muted" />
                )}
                <p className="text-sm font-medium text-ink-primary">
                  {folderDocsQ.isLoading ? (
                    'Checking folder…'
                  ) : docTypeLocked && existingSlot && existingSlotVersionsQ.isLoading ? (
                    'Checking document type lock…'
                  ) : canUpload ? (
                    <>
                      Drag a file here or <span className="text-accent-700 underline">browse</span>
                    </>
                  ) : catalogLocked ? (
                    `"${documentType}" is a locked document type — no new versions accepted`
                  ) : (
                    'Select a document type above first'
                  )}
                </p>
                <p className="text-xs text-ink-muted">
                  {catalogLocked
                    ? 'Contact your site admin if a replacement is required.'
                    : `PDF only, max ${MAX_MB} MB`}
                </p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFile(file);
                e.target.value = '';
              }}
              disabled={!canUpload}
            />
          </div>
        )}

        {/* Advanced options */}
        <div className="border-t border-surface-border pt-2">
          <button
            type="button"
            className="flex w-full items-center justify-between text-xs text-ink-muted hover:text-ink-primary"
            onClick={() => setAdvancedOpen((v) => !v)}
            disabled={isUploading}
          >
            <span>Advanced options (title, PHI, assign to…)</span>
            {advancedOpen ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </button>

          {advancedOpen && (
            <div className="mt-3 space-y-3">
              <div>
                <label className="label text-xs" htmlFor="new-doc-title">
                  Title (optional)
                </label>
                <input
                  id="new-doc-title"
                  className="input"
                  maxLength={200}
                  value={title}
                  placeholder={documentType ?? 'Falls back to document type name'}
                  disabled={isUploading}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
              <label className="flex items-center gap-2 text-xs text-ink-primary">
                <input
                  type="checkbox"
                  checked={phiPresent}
                  disabled={isUploading}
                  onChange={(e) => setPhiPresent(e.target.checked)}
                />
                Contains PHI (Protected Health Information)
              </label>
              <div>
                <label className="label text-xs" htmlFor="new-doc-assignee">
                  Route to inbox (optional)
                </label>
                <SingleSelectSearch
                  id="new-doc-assignee"
                  options={assigneeOptions}
                  value={assigneeUserId}
                  onChange={setAssigneeUserId}
                  placeholder="Leave blank to skip…"
                  disabled={isUploading}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
